import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { debounce } from '@mui/material';
import * as Ably from 'ably';
import { useChannel } from 'ably/react';

import { useAgencyStore, useCFSNavigationStore, useUserStore } from '@/store';
import {
  refreshCFSNavigation,
  shouldRefreshCFSNavigationOnEvent,
} from '@/utils/cfsNavigation';

const NAVIGATION_REFRESH_DEBOUNCE_MS = 750;

const getRouteSource = (pathname: string) => {
  if (pathname.startsWith('/cfs-active')) {
    return 'cfs-active';
  }

  if (pathname.startsWith('/cfs')) {
    return 'cfs';
  }

  return null;
};

export const useSyncCFSNavigation = (cfsId: string) => {
  const { pathname } = useLocation();
  const { account } = useUserStore();
  const { agencies } = useAgencyStore();
  const navigationContext = useCFSNavigationStore(
    (state) => state.navigationContext,
  );

  const routeSource = getRouteSource(pathname);
  const canRefreshNavigation =
    !!cfsId && !!routeSource && navigationContext?.sourceRoute === routeSource;

  const refreshNavigation = useCallback(async () => {
    if (!canRefreshNavigation) {
      return null;
    }

    try {
      return await refreshCFSNavigation({
        currentCfsId: cfsId,
        agencies,
        navigationContext,
      });
    } catch {
      return null;
    }
  }, [agencies, canRefreshNavigation, cfsId, navigationContext]);

  const debouncedRefresh = useMemo(() => {
    return debounce(() => {
      void refreshNavigation();
    }, NAVIGATION_REFRESH_DEBOUNCE_MS);
  }, [refreshNavigation]);

  useChannel(
    `account:${account?._id}:cfs:all`,
    useCallback(
      (message: Ably.Types.Message) => {
        if (!canRefreshNavigation) {
          return;
        }

        if (!shouldRefreshCFSNavigationOnEvent(message.name)) {
          return;
        }

        debouncedRefresh();
      },
      [canRefreshNavigation, debouncedRefresh],
    ),
  );

  useEffect(() => {
    return () => {
      debouncedRefresh.clear();
    };
  }, [debouncedRefresh]);

  return {
    refreshNavigation,
  };
};
